// The down payment chains shared by the coverage, quote and payment screens.
//
// A down payment cannot be derived locally — it is whatever the rating engine
// says is due today — so every function here returns null when the response
// carries none of the fields, and callers render a placeholder rather than a
// made-up number.
//
// Note `policy.downPayment` is NOT consulted: it appeared only in QuotePayment
// and is not a field the rating engine returns. Use downPaymentMonthly for the
// monthly plan and downPaymentSingle for the paid-in-full plan.

import { paidInFullFees } from './fees.js';

const toAmount = value => {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const round = value => Math.round((value + Number.EPSILON) * 100) / 100;

// findBilling quotes the paid-in-full plan as the installment option with no
// installments (installmentCount 0), whose single installment is number 0.
const firstInstallmentTotalDue = findBilling => {
  if (!findBilling || !Array.isArray(findBilling.allInstallmentOptions)) {
    return null;
  }
  const option = findBilling.allInstallmentOptions.find(
    opt => opt.installmentCount === 0
  );
  const installments = (option && option.installments) || [];
  const installment = installments.find(item => item.installmentNumber === 0);
  return installment ? toAmount(installment.totalDue) : null;
};

// Monthly plan, "Due Today".
export function monthlyDownPayment(policy) {
  if (!policy) {
    return null;
  }
  const fromPolicy = toAmount(policy.downPaymentMonthly);
  if (fromPolicy !== null) {
    return fromPolicy;
  }
  return policy.findBilling ? toAmount(policy.findBilling.downPayment) : null;
}

// Paid-in-full plan, the single payment covering the whole term.
//
// The first two levels are rated figures that already include fees. Only the
// estimatedTotalWritten fallback is a bare premium, so fees are added there
// and nowhere else.
export function paidInFullDownPayment(policy, state) {
  if (!policy) {
    return null;
  }
  const installmentTotal = firstInstallmentTotalDue(policy.findBilling);
  if (installmentTotal !== null) {
    return installmentTotal;
  }
  const fromPolicy = toAmount(policy.downPaymentSingle);
  if (fromPolicy !== null) {
    return fromPolicy;
  }
  const totalWritten = toAmount(policy.estimatedTotalWritten);
  return totalWritten === null
    ? null
    : round(totalWritten + paidInFullFees(state));
}
